import { useAuth } from "wasp/client/auth";
import { updateCurrentUser } from "wasp/client/operations";
import "./Main.css";
import CookieConsentBanner from "./components/cookie-consent/Banner";
import { useMemo, useEffect, ReactNode } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { useColorMode } from "./context/ColorModeContext";
import { ColorModeProvider } from "./context/ColorModeContext";
import { Provider } from "react-redux";
import { store } from "../store/store";
import { initSentryFrontend } from "../utils/sentry";
import { PersistGate } from "redux-persist/integration/react";
import { persistor } from "../store/store";
import { seedConfig } from "wasp/client/operations";
import {
  useSocket,
  useSocketListener,
  ServerToClientPayload,
} from 'wasp/client/webSocket'
import { analytics } from "../analytics";
import { useSearchParams } from "react-router-dom";
import { initHotjar } from "../hotjarConfig";
import { Loader2 } from "lucide-react";
import { Suspense } from "react";

initSentryFrontend();

export default function App() {
  useEffect(() => {
    initHotjar();
  },[])
  return (
    <Suspense fallback={<div className="flex items-center justify-center h-screen"><Loader2 className="animate-spin" /> </div>}>
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
      <ColorModeProvider>
        <AppInner />
      </ColorModeProvider>
      </PersistGate>
    </Provider>
    </Suspense>
  );
}


const AppInner = () => {
  const { colorMode } = useColorMode();

  const location = useLocation();
  const { data: user } = useAuth();

  const isAdminDashboard = useMemo(() => {
    return location.pathname.startsWith("/admin") || location.pathname === "/";
  }, [location]);

  useEffect(() => {
    if (user) {
      const lastSeenAt = new Date(user.lastActiveTimestamp);
      const today = new Date();
      if (today.getTime() - lastSeenAt.getTime() > 5 * 60 * 1000) {
        updateCurrentUser({ lastActiveTimestamp: today });
      }
    }
  }, [user]);

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace("#", "");
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView();
      }
    }

    const searchParams = new URLSearchParams(location.search);
    const shop = searchParams.get("shop");
    const hmac = searchParams.get("hmac");

    if (shop && hmac) {
      document.cookie = `shop=${shop};path=/;domain=${window.location.hostname};SameSite=Lax;secure`;
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [location]);

  useEffect(() => {
    let keySequence: string[] = [];
    const handleKeyDown = (e: KeyboardEvent) => {
      keySequence.push(e.key);
      if (keySequence.join("").includes("dddddd")) {
        localStorage.setItem("debug_mode", "true");
        window.location.reload();
        keySequence = [];
      } else if (keySequence.join("").includes("llllll")) {
        localStorage.setItem("debug_mode", "false");
        window.location.reload();
        keySequence = [];
      }
      setTimeout(() => {
        keySequence.shift();
      }, 2000);
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  if (
    window.location.pathname.includes("upgrade-plan") &&
    window.location.search.length !== 0
  ) {
    localStorage.setItem(
      "redirect_url",
      window.location.pathname + window.location.search
    );
  }

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const error = params.get("error");
    if (error?.includes("identity already exists")) {
      window.location.href = `/login?error=already-signed-in-with-email`;
    }
  }, []);

  useEffect(() => {
    const seed = async () => {
      await seedConfig();
    }
    seed();
  }, [])


  //SOCKETS

  const { socket, isConnected } = useSocket()
  useSocketListener('login', (msg) => {
    analytics.sendLoginEvent(msg)
  })
  useSocketListener('signup_completed', (msg) => {
    analytics.sendSignupCompletedEvent(msg)
  })


    const [searchParams, setSearchParams] = useSearchParams();
    useEffect(() => {
      const queryParams = new URLSearchParams(window.location.search);
      const referralSource = queryParams.get("register_rs");
      if (referralSource) {
        searchParams.delete("register_rs");
        setSearchParams(searchParams);
        localStorage.setItem("register_rs", referralSource);
      }
    },[]);


  return (
    <>
        <div className="dark:text-white dark:bg-boxdark-2">
          {isAdminDashboard ? (
            <Outlet />
          ) : (
            <>
              <div className="">
                <Outlet />
              </div>
            </>
          )}
        </div>
        <CookieConsentBanner />
    </>
  );
};
